import { useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight, Star, Quote } from "lucide-react";
import { REVIEWS } from "@/lib/data";

const colorPool = [
  ["bg-brand-soft", "text-brand-blue"],
  ["bg-[#F2F9DD]", "text-brand-aquaDark"],
  ["bg-[#FFF5E5]", "text-amber-700"],
  ["bg-[#FCE9F3]", "text-pink-700"],
];

function Avatar({ name, idx }) {
  const initial = name.charAt(0).toUpperCase();
  const [bg, fg] = colorPool[idx % colorPool.length];
  return (
    <div className={`w-12 h-12 rounded-full flex items-center justify-center font-display font-medium ${bg} ${fg} text-[18px]`}>
      {initial}
    </div>
  );
}

function ReviewCard({ name, text, idx }) {
  return (
    <div
      data-testid={`review-card-${idx}`}
      className="h-full bg-white rounded-3xl p-7 lg:p-8 border border-slate-100 card-shadow flex flex-col"
    >
      <Quote className="w-8 h-8 text-brand-blue/30 mb-4" strokeWidth={1.5} />
      <p className="text-[15px] lg:text-[16px] leading-relaxed text-slate-700 flex-grow">
        “{text}”
      </p>
      <div className="mt-6 pt-5 border-t border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Avatar name={name} idx={idx} />
          <div>
            <p className="font-display font-medium text-[15px] text-slate-900">{name}</p>
            <p className="text-[12px] text-slate-500">Pacient Marimar Clinic</p>
          </div>
        </div>
        <div className="flex gap-0.5">
          {[...Array(5)].map((_, i) => (
            <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Reviews() {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: "start",
    slidesToScroll: 1,
    containScroll: "trimSnaps",
  });
  const [selected, setSelected] = useState(0);
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelected(emblaApi.selectedScrollSnap());
    setCount(emblaApi.scrollSnapList().length);
    emblaApi.on("select", onSelect);
    onSelect();
    const interval = setInterval(() => emblaApi.scrollNext(), 5500);
    return () => {
      clearInterval(interval);
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi]);

  return (
    <section
      id="recenzii"
      data-testid="reviews-section"
      className="relative py-20 lg:py-28 bg-white overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.22em] font-semibold text-brand-blue mb-5">
              <span className="w-6 h-px bg-brand-blue" />
              Recenzii pacienți
            </span>
            <h2 className="font-display text-[34px] sm:text-[42px] lg:text-[52px] leading-[1.05] font-light text-slate-900 tracking-tight text-balance">
              Ce spun cei care au trecut <span className="text-gradient-blue font-medium">pragul clinicii</span>.
            </h2>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-brand-soft border border-blue-100">
              <div className="flex gap-0.5">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                ))}
              </div>
              <span className="text-[13px] font-medium text-slate-700">30+ recenzii reale</span>
            </div>
            <button
              onClick={() => emblaApi && emblaApi.scrollPrev()}
              className="w-11 h-11 rounded-full bg-white border border-slate-200 hover:border-brand-blue hover:text-brand-blue text-slate-700 flex items-center justify-center transition-colors"
              aria-label="Recenzia anterioară"
              data-testid="reviews-prev"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={() => emblaApi && emblaApi.scrollNext()}
              className="w-11 h-11 rounded-full bg-brand-blue text-white hover:bg-brand-blueDark flex items-center justify-center transition-colors shadow-lg shadow-brand-blue/20"
              aria-label="Recenzia următoare"
              data-testid="reviews-next"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="overflow-hidden -mx-3" ref={emblaRef} data-testid="reviews-carousel">
          <div className="flex">
            {REVIEWS.map((r, i) => (
              <div
                key={r.name + i}
                className="shrink-0 px-3 w-full sm:w-1/2 lg:w-1/3"
              >
                <ReviewCard {...r} idx={i} />
              </div>
            ))}
          </div>
        </div>

        <div className="mt-8 flex justify-center gap-1.5">
          {Array.from({ length: count }).slice(0, 12).map((_, i) => (
            <button
              key={i}
              onClick={() => emblaApi && emblaApi.scrollTo(i)}
              className={`h-1.5 rounded-full transition-all ${
                selected === i ? "w-8 bg-brand-blue" : "w-1.5 bg-slate-300"
              }`}
              aria-label={`Mergi la recenzia ${i + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
